import { turnstileEnabled } from "@circle-react/components/OnboardingPage/helpers/methods";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { useCallback, useReducer, useRef } from "react";

export type TurnstileActionType =
  | "SET_FAILED"
  | "SET_SUCCESS"
  | "SET_INTERACTIVE";

interface TurnstileAction {
  type: TurnstileActionType;
}

interface TurnstileState {
  hasFailed: boolean;
  isInteractionSuccessful: boolean;
  isLoading: boolean;
  requiresInteraction: boolean;
}

const SET_FAILED = "SET_FAILED";
const SET_SUCCESS = "SET_SUCCESS";
const SET_INTERACTIVE = "SET_INTERACTIVE";

const turnstileReducer = (
  state: TurnstileState,
  action: TurnstileAction,
): TurnstileState => {
  switch (action.type) {
    case SET_FAILED:
      return {
        ...state,
        isLoading: false,
        hasFailed: true,
        requiresInteraction: false,
        isInteractionSuccessful: false,
      };
    case SET_SUCCESS:
      return {
        ...state,
        isLoading: false,
        hasFailed: false,
        requiresInteraction: state.requiresInteraction,
        isInteractionSuccessful: state.requiresInteraction,
      };
    case SET_INTERACTIVE:
      return {
        ...state,
        isLoading: false,
        hasFailed: false,
        requiresInteraction: true,
        isInteractionSuccessful: false,
      };
    default:
      return state;
  }
};

interface UseTurnstileReturn {
  canUseTurnstile: boolean;
  dispatch: React.Dispatch<TurnstileAction>;
  resetTurnstileToken: () => void;
  shouldShowTurnstile: boolean;
  shouldUseTurnstile: boolean;
  turnstileRef: React.RefObject<TurnstileInstance | null>;
  turnstileResult: TurnstileState;
  turnstileSiteKey: string;
}

export const useTurnstile = (forceDisabled = false): UseTurnstileReturn => {
  const canUseTurnstile = !forceDisabled && turnstileEnabled(window.RAILS_ENV);
  const turnstileSiteKey = window.TURNSTILE_SITE_KEY || "";
  const shouldUseTurnstile = canUseTurnstile && Boolean(turnstileSiteKey);

  const initialTurnstileState: TurnstileState = {
    isLoading: canUseTurnstile,
    hasFailed: false,
    requiresInteraction: false,
    isInteractionSuccessful: false,
  };

  const turnstileRef = useRef<TurnstileInstance>(null);
  const [turnstileResult, dispatch] = useReducer(
    turnstileReducer,
    initialTurnstileState,
  );
  const resetTurnstileToken = useCallback(() => {
    turnstileRef.current?.reset();
  }, []);
  const shouldShowTurnstile =
    canUseTurnstile &&
    !turnstileResult.isLoading &&
    (turnstileResult.requiresInteraction || turnstileResult.hasFailed);

  return {
    turnstileRef,
    turnstileResult,
    dispatch,
    canUseTurnstile,
    resetTurnstileToken,
    shouldShowTurnstile,
    shouldUseTurnstile,
    turnstileSiteKey,
  };
};
